import cn from 'classnames';
import { ReactNode, useMemo } from 'react';
import { KnowYourCustomerData } from '@blisspointmedia/bpm-types/dist/KnowYourCustomer';
import { WidgetContainer } from '@Components/WidgetContainer';
import { ChartContainer } from '@src/Components/ChartContainer';
import { Neutral300, Neutral400 } from '@src/utils/colors';
import {
  CUSTOMER_INSIGHTS_LEVELS_PRETTY_NAMES,
  CUSTOMER_INSIGHTS_PRETTY_NAMES,
  CustomerInsightsFields,
  SortOptions,
  TabKey,
  ViewOptions
} from './customer-insights-constants';
import { getCustomerInsightsAlphabeticalSort } from './customer-insights-utils';
import { CustomerBaseChart } from './CustomerBaseChart';
import { CustomerInsightsTable } from './CustomerInsightsTable';
import { IndexChart } from './IndexChart';
import { PenetrationChart } from './PenetrationChart';

const CustomerKey = () => (
  <>
    <div className='key-filled customer-key' />
    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
      <path
        d='M9 0C10.1819 1.4094e-08 11.3522 0.232792 12.4442 0.685084C13.5361 1.13738 14.5282 1.80031 15.364 2.63604C16.1997 3.47177 16.8626 4.46392 17.3149 5.55585C17.7672 6.64778 18 7.8181 18 9L9 9V0Z'
        fill={Neutral400}
      />
    </svg>
    <span>Customer</span>
  </>
);

const USMarketKey = () => (
  <>
    <div className='key-filled market-key' />
    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
      <circle cx='9' cy='9' r='9' fill={Neutral300} />
      <path
        d='M9 0C10.1819 1.4094e-08 11.3522 0.232792 12.4442 0.685084C13.5361 1.13738 14.5282 1.80031 15.364 2.63604C16.1997 3.47177 16.8626 4.46392 17.3149 5.55585C17.7672 6.64778 18 7.8181 18 9L9 9V0Z'
        fill={Neutral400}
      />
    </svg>
    <div>US Market</div>
  </>
);

interface CustomerInsightsGroupProps {
  fields: CustomerInsightsFields[];
  data: Record<string, KnowYourCustomerData[]>;
  header: string | ReactNode;
  sortValue: SortOptions;
  graphView: ViewOptions;
  tab: TabKey;
  suppressUnknown: boolean;
  dataTestId: string;
}

export const CustomerInsightsGroup = ({
  fields,
  data,
  header,
  sortValue,
  graphView,
  tab,
  suppressUnknown,
  dataTestId
}: CustomerInsightsGroupProps) => {
  const sortedData = useMemo(() => {
    const dataToSort = structuredClone(data);

    if (sortValue === SortOptions.ALPHABETICAL) {
      Object.keys(dataToSort).forEach(key => {
        const fieldKey = key as CustomerInsightsFields;
        dataToSort[key] = getCustomerInsightsAlphabeticalSort(fieldKey, dataToSort[key]);
      });
      return dataToSort;
    }

    const getProperty = (row: KnowYourCustomerData): number => {
      switch (tab) {
        case TabKey.CUSTOMER_BASE:
          return row.clientPopulation;
        case TabKey.INDEX:
          return row.clientPopulation / row.tuPopulation;
        case TabKey.PENETRATION:
          return row.clientPopulationFraction / row.tuPopulationFraction;
        default:
          return -1;
      }
    };

    const compare =
      sortValue === SortOptions.HIGH_TO_LOW
        ? (a: KnowYourCustomerData, b: KnowYourCustomerData) => getProperty(b) - getProperty(a)
        : (a: KnowYourCustomerData, b: KnowYourCustomerData) => getProperty(a) - getProperty(b);

    Object.keys(dataToSort).forEach(fieldName => {
      dataToSort[fieldName] = [...dataToSort[fieldName]].sort(compare);

      Object.keys(CUSTOMER_INSIGHTS_LEVELS_PRETTY_NAMES[fieldName]).forEach(dataName => {
        const prettyName = CUSTOMER_INSIGHTS_LEVELS_PRETTY_NAMES[fieldName][dataName];
        const valsIndex = dataToSort[fieldName].findIndex(item => item.levels === dataName);
        if (valsIndex !== -1) {
          dataToSort[fieldName][valsIndex].levels = prettyName;
        }
      });
    });

    return dataToSort;
  }, [data, sortValue, tab]);

  const flattenedData = Object.values(sortedData).flat();
  const maxPenetration = flattenedData.reduce(
    (acc, val) => Math.max(acc, (val.clientPopulation / val.tuPopulation) * 100),
    0
  );
  const minPenetration = flattenedData.reduce(
    (acc, val) => Math.min(acc, (val.clientPopulation / val.tuPopulation) * 100),
    1
  );
  let penetrationDecimalPrecision: number;
  if (maxPenetration > 0.5 && (minPenetration >= 0.01 || minPenetration === 0)) {
    penetrationDecimalPrecision = 1;
  } else if (maxPenetration > 0.05 && (minPenetration >= 0.001 || minPenetration === 0)) {
    penetrationDecimalPrecision = 2;
  } else {
    penetrationDecimalPrecision = 3;
  }

  const renderGraph = (field: string): ReactNode => {
    const fieldName = field as CustomerInsightsFields;
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return <CustomerBaseChart fieldName={fieldName} data={sortedData[field]} />;
      case TabKey.INDEX:
        return <IndexChart fieldName={fieldName} data={sortedData[field]} />;
      case TabKey.PENETRATION:
        return (
          <PenetrationChart
            fieldName={fieldName}
            data={sortedData[field]}
            decimalPrecision={penetrationDecimalPrecision}
          />
        );
      default:
        return undefined;
    }
  };

  const renderTable = (field: string): ReactNode => {
    const fieldPrettyName = CUSTOMER_INSIGHTS_PRETTY_NAMES[field];
    return (
      <ChartContainer title={fieldPrettyName}>
        <CustomerInsightsTable tab={tab} fieldData={sortedData[field]} />
      </ChartContainer>
    );
  };

  return (
    <WidgetContainer
      dataTestId={dataTestId}
      header={header}
      rightActions={
        tab === TabKey.INDEX || tab === TabKey.PENETRATION ? (
          <div className='customer-insights-group-actions'>
            <div className='customer-insights-key'>
              <span className='customer-insights-key-item'>
                <CustomerKey />
              </span>
              <span className='customer-insights-key-item'>
                <USMarketKey />
              </span>
            </div>
          </div>
        ) : undefined
      }
      collapsible
      headerCollapsibleOnClick
    >
      {fields.map(field => (
        <div
          key={field}
          className={cn('customer-insights-group', {
            'full-width': field === CustomerInsightsFields.INCOME,
            'income-unknown-height': field === CustomerInsightsFields.INCOME && !suppressUnknown,
            'income-no-unknown-height': field === CustomerInsightsFields.INCOME && suppressUnknown,
            'fixed-height': field !== CustomerInsightsFields.INCOME
          })}
        >
          {graphView === ViewOptions.SHOW_GRAPH && renderGraph(field)}
          {graphView === ViewOptions.SHOW_TABLE && renderTable(field)}
        </div>
      ))}
    </WidgetContainer>
  );
};
